import { useState, useEffect } from "react";

interface Coordinates {
  latitude: number;
  longitude: number;
}

function useDistanceCalculator() {
  const [userLocation, setUserLocation] = useState<Coordinates | null>(null);

  interface Coordinates {
    latitude: number;
    longitude: number;
  }

  useEffect(() => {
    // Get user's current location
    if ("geolocation" in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          setUserLocation({ latitude, longitude });
        },
        (error) => {
          console.error("Error getting user location:", error);
        }
      );
    } else {
      console.error("Geolocation is not supported");
    }
  }, []);

  // const calculateDistance = (
  //   lat1: number,
  //   lon1: number,
  //   lat2: number,
  //   lon2: number
  // ): number => {
  //   // const R = 6371; // Radius of the Earth in kilometers
  //   const R = 3958; // Radius of the Earth in miles
  //   const dLat = toRadians(lat2 - lat1);
  //   const dLon = toRadians(lon2 - lon1);
  //   const a =
  //     Math.sin(dLat / 2) * Math.sin(dLat / 2) +
  //     Math.cos(toRadians(lat1)) *
  //       Math.cos(toRadians(lat2)) *
  //       Math.sin(dLon / 2) *
  //       Math.sin(dLon / 2);
  //   const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  //   const distance = R * c; // Distance in kilometers
  //   return distance;
  // };

  const calculateDistance = (
    lat1: number,
    lon1: number,
    lat2: number,
    lon2: number
  ) => {
    const result = DistanceCalculator.distanceBetween(lat1, lon1, lat2, lon2);
    // console.log("result", result);
    // return DistanceCalculator.convertDistance(result);
    return result / 1609.34;
  };

  function calculateDistanceVincenty(
    lat1: number,
    lon1: number,
    lat2: number,
    lon2: number
  ): number {
    const a = 6378137; // Earth's equatorial radius in meters
    const f = 1 / 298.257223563; // Earth's flattening
    const b = (1 - f) * a; // Earth's polar radius in meters

    const phi1 = toRadians(lat1);
    const phi2 = toRadians(lat2);
    const lambda1 = toRadians(lon1);
    const lambda2 = toRadians(lon2);

    const U1 = Math.atan((1 - f) * Math.tan(phi1));
    const U2 = Math.atan((1 - f) * Math.tan(phi2));
    const L = lambda2 - lambda1;

    let lambda = L;
    let lambdaPrev = 0;
    let cosAlpha = 0;
    let sinSigma = 0;
    let cosSigma = 0;
    let sigma = 0;

    const MAX_ITERATIONS = 100;
    let iteration = 0;

    let cos2SigmaM = 0;

    while (
      Math.abs(lambda - lambdaPrev) > 1e-12 &&
      iteration < MAX_ITERATIONS
    ) {
      iteration++;
      const sinLambda = Math.sin(lambda);
      const cosLambda = Math.cos(lambda);

      const sinU1 = Math.sin(U1);
      const cosU1 = Math.cos(U1);
      const sinU2 = Math.sin(U2);
      const cosU2 = Math.cos(U2);

      const sinSigmaPrev = sinSigma;
      sinSigma = Math.sqrt(
        (cosU2 * sinLambda) ** 2 +
          (cosU1 * sinU2 - sinU1 * cosU2 * cosLambda) ** 2
      );
      cosSigma = sinU1 * sinU2 + cosU1 * cosU2 * cosLambda;
      sigma = Math.atan2(sinSigma, cosSigma);

      const sinAlpha = (cosU1 * cosU2 * sinLambda) / sinSigma;
      cosAlpha = 1 - sinAlpha ** 2;

      cos2SigmaM = cosSigma - (2 * sinU1 * sinU2) / cosAlpha;

      const C = (f / 16) * cosAlpha * (4 + f * (4 - 3 * cosAlpha));
      lambdaPrev = lambda;
      lambda =
        L +
        (1 - C) *
          f *
          sinAlpha *
          (sigma +
            C *
              sinSigma *
              (cos2SigmaM + C * cosSigma * (-1 + 2 * cos2SigmaM ** 2)));
    }

    if (iteration >= MAX_ITERATIONS) {
      console.error("Vincenty formula failed to converge.");
      return NaN;
    }

    const u2 = cosAlpha * ((a ** 2 - b ** 2) / b ** 2);
    const A = 1 + (u2 / 16384) * (4096 + u2 * (-768 + u2 * (320 - 175 * u2)));
    const B = (u2 / 1024) * (256 + u2 * (-128 + u2 * (74 - 47 * u2)));
    const deltaSigma =
      B *
      sinSigma *
      (cos2SigmaM +
        (B / 4) *
          (cosSigma * (-1 + 2 * cos2SigmaM ** 2) -
            (B / 6) *
              cos2SigmaM *
              (-3 + 4 * sinSigma ** 2) *
              (-3 + 4 * cos2SigmaM ** 2)));

    const distance = b * A * (sigma - deltaSigma); // Distance in meters

    // return distance;

    // Conversion from meters to miles
    const distanceInMiles = distance / 1609.34; // 1 mile = 1609.34 meters
    // const distanceInMiles = distance * 0.000621371;

    return distanceInMiles;
  }
  const toRadians = (degrees: number): number => {
    return degrees * (Math.PI / 180);
  };

  const getDistanceFromUserInVicenty = (
    latitude: number,
    longitude: number
  ): number | null => {
    if (!userLocation) {
      console.error("User location not available yet.");
      // return null;
    }
    const distance = calculateDistanceVincenty(
      // userLocation.latitude,
      // userLocation.longitude,
      40.7128,
      -74.006,
      // 40.45533299999999,
      // -74.41694679999999,
      latitude,
      longitude
    );
    return distance;
  };

  const getDistanceFromUser = (
    latitude: number,
    longitude: number
  ): number | string | null => {
    if (!userLocation) {
      console.error("User location not available yet.");
      // return null;
    }
    // console.log("userLocation", userLocation);
    return calculateDistance(
      // TODO: Switch to user location
      //   userLocation.latitude,
      //   userLocation.longitude,
      // 40.45533299999999,
      // -74.41694679999999,
      40.7128,
      -74.006,
      latitude,
      longitude
    );
  };

  const getDistanceFromSelectedLocation = (
    origin: {
      latitude: number;
      longitude: number;
    },
    parkedLocation: {
      latitude: number;
      longitude: number;
    }
  ): number | null => {
    return calculateDistanceVincenty(
      origin.latitude,
      origin.longitude,
      parkedLocation.latitude,
      parkedLocation.longitude
    );
  };

  return {
    userLocation,
    getDistanceFromUser,
    getDistanceFromUserInVicenty,
    getDistanceFromSelectedLocation,
    calculateDistance,
  };
}

export class DistanceCalculator {
  static distanceBetween(
    startLatitude: number,
    startLongitude: number,
    endLatitude: number,
    endLongitude: number
  ): number {
    const earthRadius = 6378137.0;
    const dLat = DistanceCalculator.toRadians(endLatitude - startLatitude);
    const dLon = DistanceCalculator.toRadians(endLongitude - startLongitude);

    const a =
      Math.pow(Math.sin(dLat / 2), 2) +
      Math.pow(Math.sin(dLon / 2), 2) *
        Math.cos(DistanceCalculator.toRadians(startLatitude)) *
        Math.cos(DistanceCalculator.toRadians(endLatitude));
    const c = 2 * Math.asin(Math.sqrt(a));

    return earthRadius * c;
  }

  static convertDistance(meters: number): string {
    const metersInMile = 1609.34;
    const metersInFoot = 0.3048;

    let miles = meters / metersInMile;
    let feet = meters / metersInFoot;

    if (miles < 0.1) {
      return `${Math.round(feet)} ft`;
    } else {
      return `${miles.toFixed(2)} mi`;
    }
  }

  static toRadians(degree: number): number {
    return (degree * Math.PI) / 180;
  }
}

export default useDistanceCalculator;
